import React from 'react';

import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import PillGroup from 'components/PillGroup';
import VenueMultiSelector from 'components/VenueMultiSelector';
import { venuePropTypes } from 'constants/propTypes';
import { bem } from 'helpers/class-name';

import { DATE_RANGE_PRESET, presetRange } from './SkuOrderReports.filters';
import messages from './SkuOrderReports.messages';
import SkuReportDateRangeFilter from './SkuReportDateRangeFilter';
import {
  DATE_SELECTION_TYPE,
  FILTER_TYPE,
  presetLabel,
  rangeLengthDays,
  useReportsTracking,
} from './SkuReports.tracking';
import './SkuOrderReportsFilters.scss';

const { block, element } = bem('sku-order-reports-filters');

const PRESET_MESSAGES = {
  [DATE_RANGE_PRESET.LAST_7_DAYS]: messages.last7Days,
  [DATE_RANGE_PRESET.LAST_30_DAYS]: messages.last30Days,
  [DATE_RANGE_PRESET.LAST_6_MONTHS]: messages.last6Months,
};

const SkuOrderReportsFilters = ({
  venues,
  selectedVenueIds,
  onVenuesChange,
  range,
  onRangeChange,
  preset,
  onPresetSelect,
}) => {
  const intl = useIntl();
  const tracking = useReportsTracking();

  const presetOptions = Object.entries(PRESET_MESSAGES).map(([key, label]) => ({
    key,
    label: intl.formatMessage(label),
  }));

  const handleVenuesChange = updatedVenueIds => {
    const venueProps = {
      'venue ids': updatedVenueIds,
      'venue count': updatedVenueIds.length,
      'is multi venue': updatedVenueIds.length > 1,
    };
    if (updatedVenueIds.length === venues.length) {
      tracking.filterRemoved(FILTER_TYPE.LOCATION, venueProps);
    } else {
      tracking.filterApplied(FILTER_TYPE.LOCATION, { ...venueProps, 'venues selected': updatedVenueIds.length });
    }
    onVenuesChange(updatedVenueIds);
  };

  const handlePresetSelect = key => {
    tracking.dateRangeSelected({
      selectionType: DATE_SELECTION_TYPE.PRESET,
      range: presetLabel(key),
      rangeDays: rangeLengthDays(presetRange(key)),
    });
    onPresetSelect(key);
  };

  return (
    <div className={block()}>
      <div className={element('venues')}>
        <VenueMultiSelector
          attachment="left"
          disabled={venues.length < 2}
          hideSingleVenue={false}
          labels={{
            label: messages.locationLabel,
            allSelected: messages.allLocations,
            selectedCount: messages.locationsSelected,
          }}
          onChange={handleVenuesChange}
          onOpen={() => tracking.filterOpened(FILTER_TYPE.LOCATION)}
          selectedVenueIds={selectedVenueIds}
          venues={venues}
        />
      </div>
      <div className={element('date')}>
        <SkuReportDateRangeFilter value={range} onChange={onRangeChange} />
      </div>
      <PillGroup
        className={element('presets')}
        options={presetOptions}
        value={preset}
        onChange={key => {
          if (key !== preset) handlePresetSelect(key);
        }}
      />
    </div>
  );
};

SkuOrderReportsFilters.propTypes = {
  venues: PropTypes.arrayOf(PropTypes.shape(venuePropTypes)).isRequired,
  selectedVenueIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  onVenuesChange: PropTypes.func.isRequired,
  range: PropTypes.shape({
    from: PropTypes.instanceOf(Date).isRequired,
    to: PropTypes.instanceOf(Date).isRequired,
  }).isRequired,
  onRangeChange: PropTypes.func.isRequired,
  preset: PropTypes.oneOf(Object.values(DATE_RANGE_PRESET)),
  onPresetSelect: PropTypes.func.isRequired,
};

export default SkuOrderReportsFilters;
